import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Header from "../../components/Header";
import ConfirmDialog from "../../components/ConfirmDialog";
import ReportOrderModal from "../../components/ReportOrderModal";
import api from "../../utils/api";
import { getImageUrl } from "../../utils/imageUtils";
import { formatFarmerStreetAddress } from "../../utils/addressUtils";
import {
  toLocalizedNumber,
  toLocalizedPrice,
  toLocalizedStatus,
} from "../../utils/localeUtils";
import { usePopup } from "../../context/PopupContext";
import {
  FaSearch,
  FaTimes,
  FaChevronDown,
  FaChevronUp,
  FaShoppingBag,
  FaTruck,
  FaCheckCircle,
  FaHourglassHalf,
  FaClock,
  FaTimesCircle,
  FaMapMarkerAlt,
  FaComments,
  FaFlag,
  FaCreditCard,
  FaArrowRight,
  FaBoxOpen,
  FaHandHolding,
  FaObjectGroup,
  FaCheckDouble,
  FaArchive,
  FaTrash,
  FaUndo,
} from "react-icons/fa";
import { ROLE_IMG } from "../../utils/roleAssets";
import Pagination from "../../components/Pagination";
import "./MyOrders.css";

const PER_PAGE = 10;

const statusMeta = {
  pending: { icon: FaHourglassHalf, color: "#f59e0b", bg: "#fffbeb" },
  confirmed: { icon: FaCheckCircle, color: "#3b82f6", bg: "#eff6ff" },
  processing: { icon: FaClock, color: "#8b5cf6", bg: "#f5f3ff" },
  shipped: { icon: FaTruck, color: "#06b6d4", bg: "#ecfeff" },
  outForDelivery: { icon: FaTruck, color: "#06b6d4", bg: "#ecfeff" },
  delivered: { icon: FaCheckCircle, color: "#22c55e", bg: "#f0fdf4" },
  received: { icon: FaHandHolding, color: "#0d9488", bg: "#f0fdfa" },
  completed: { icon: FaCheckDouble, color: "#15803d", bg: "#ecfdf5" },
  cancelled: { icon: FaTimesCircle, color: "#ef4444", bg: "#fef2f2" },
};

const MyOrders = () => {
  const { showToast } = usePopup();
  const { t } = useTranslation();
  const [filter, setFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedOrders, setExpandedOrders] = useState(new Set());
  const [reportOrderId, setReportOrderId] = useState(null);
  const [reportOrderNumber, setReportOrderNumber] = useState(null);
  const [payingOrderId, setPayingOrderId] = useState(null);
  const [confirmingOrderId, setConfirmingOrderId] = useState(null);
  const [mergingOrders, setMergingOrders] = useState(false);
  const [selectedMerge, setSelectedMerge] = useState(new Set());
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: null,
  });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [listView, setListView] = useState("active");
  const [cancellingOrderId, setCancellingOrderId] = useState(null);

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/orders", {
        params: { view: listView === "archive" ? "archive" : "active" },
      });
      setOrders(
        (res.data || []).map((o) => ({
          _id: o._id,
          orderDate: new Date(o.createdAt),
          status: o.orderStatus || "pending",
          totalAmount: o.totalPrice || 0,
          paymentMethod: o.paymentMethod || "COD",
          paymentStatus: o.paymentStatus || "pending",
          items: (o.items || []).map((i) => ({
            _id: i.product?._id || i.product,
            name: i.name || i.product?.name || "Product",
            image: i.product?.images?.[0] || null,
            price: i.price || 0,
            quantity: i.quantity || 0,
            unit: i.product?.unit || "kg",
          })),
          farmer: {
            _id: o.farmer?._id || o.farmer,
            name: o.farmer?.name || "Farmer",
            location: formatFarmerStreetAddress(o.farmer),
          },
          deliveryAddress: o.shippingAddress
            ? `${o.shippingAddress.street || ""}, ${o.shippingAddress.district || ""}, ${o.shippingAddress.city || ""}`
            : "N/A",
          deliveryDate: o.deliveredAt ? new Date(o.deliveredAt) : null,
          estimatedDelivery: o.deliveryDate ? new Date(o.deliveryDate) : null,
          paymentGateway: o.paymentGateway || null,
          cancellationReason: o.cancellationReason || null,
        })),
      );
    } catch (e) {
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [listView]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const getProductImage = (item) => {
    if (item.image)
      return item.image.startsWith("http")
        ? item.image
        : getImageUrl(item.image);
    return "/images/deafultpic.png";
  };

  const filteredOrders = orders.filter((o) => {
    const sm =
      filter === "all" ||
      o.status === filter ||
      (filter === "outForDelivery" &&
        ["shipped", "outForDelivery"].includes(o.status)) ||
      (filter === "processing" &&
        ["processing", "confirmed"].includes(o.status));
    const tm =
      !searchTerm.trim() ||
      o._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (o.farmer?.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      o.items.some((i) =>
        (i.name || "").toLowerCase().includes(searchTerm.toLowerCase()),
      );
    return sm && tm;
  });

  const totalPages = Math.ceil(filteredOrders.length / PER_PAGE);
  const pageOrders = filteredOrders.slice(
    (page - 1) * PER_PAGE,
    page * PER_PAGE,
  );

  useEffect(() => {
    setPage(1);
  }, [filter, searchTerm, listView]);

  useEffect(() => {
    setExpandedOrders(new Set());
  }, [listView]);

  const canConsumerCancelOrder = (o) =>
    o.status === "pending" && o.paymentStatus !== "paid";
  const canArchiveOrder = (o) =>
    ["delivered", "received", "completed"].includes(o.status);

  const handleCancelOrder = (orderId) => {
    setConfirmDialog({
      open: true,
      title: t("myOrders.confirmCancelTitle"),
      message: t("myOrders.confirmCancelMessage"),
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        setCancellingOrderId(orderId);
        try {
          await api.put(`/orders/${orderId}/cancel`);
          showToast(t("myOrders.cancelSuccess"), "success");
          fetchOrders();
        } catch (err) {
          showToast(err.response?.data?.message || "Request failed", "error");
        } finally {
          setCancellingOrderId(null);
        }
      },
    });
  };

  const handleRemoveOrder = (orderId) => {
    setConfirmDialog({
      open: true,
      title: t("myOrders.removeFromList"),
      message: t("myOrders.confirmRemoveMessage"),
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        try {
          await api.delete(`/orders/${orderId}/consumer-list`);
          showToast(t("myOrders.removeSuccess"), "success");
          fetchOrders();
        } catch (err) {
          showToast(err.response?.data?.message || "Request failed", "error");
        }
      },
    });
  };

  const handleArchiveOrder = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/archive`);
      showToast(t("myOrders.archiveSuccess"), "success");
      fetchOrders();
    } catch (err) {
      showToast(err.response?.data?.message || "Request failed", "error");
    }
  };

  const handleUnarchiveOrder = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/unarchive`);
      showToast(t("myOrders.unarchiveSuccess"), "success");
      fetchOrders();
    } catch (err) {
      showToast(err.response?.data?.message || "Request failed", "error");
    }
  };

  const formatDate = (d) =>
    new Date(d).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const toggleOrder = (id) => {
    setExpandedOrders((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  };

  const handlePayAgain = async (orderId, gateway) => {
    setPayingOrderId(orderId);
    try {
      if (gateway === "esewa") {
        const res = await api.post("/payments/esewa/initiate", { orderId });
        const { esewaFormData } = res.data;
        if (esewaFormData) {
          const form = document.createElement("form");
          form.method = "POST";
          form.action = esewaFormData.url;
          const { url, ...fields } = esewaFormData;
          Object.entries(fields).forEach(([k, v]) => {
            const inp = document.createElement("input");
            inp.type = "hidden";
            inp.name = k;
            inp.value = v;
            form.appendChild(inp);
          });
          document.body.appendChild(form);
          form.submit();
          return;
        }
      } else {
        const res = await api.post("/payments/khalti/initiate", { orderId });
        if (res.data?.payment_url) {
          window.location.href = res.data.payment_url;
          return;
        }
      }
      throw new Error("No payment URL");
    } catch (err) {
      setPayingOrderId(null);
      showToast(
        err.response?.data?.message || t("myOrders.paymentError"),
        "error",
      );
    }
  };

  const canPayAgain = (o) =>
    (o.paymentMethod || "").toLowerCase() === "online" &&
    (o.paymentStatus || "").toLowerCase() !== "paid";

  const handleConfirmReceived = (orderId) => {
    setConfirmDialog({
      open: true,
      title: t("myOrders.confirmReceiveTitle"),
      message: t("myOrders.confirmReceiveMessage"),
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        setConfirmingOrderId(orderId);
        try {
          await api.put(`/orders/${orderId}/received`);
          showToast(t("myOrders.receivedSuccess"), "success");
          fetchOrders();
        } catch (err) {
          showToast(err.response?.data?.message || "Error", "error");
        } finally {
          setConfirmingOrderId(null);
        }
      },
    });
  };

  // Detect mergeable groups: pending orders from same farmer + same payment method
  const mergeGroups = (() => {
    const pending = orders.filter((o) => o.status === "pending");
    const groups = {};
    pending.forEach((o) => {
      const key = `${o.farmer._id}__${o.paymentMethod}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(o);
    });
    return Object.values(groups).filter((g) => g.length >= 2);
  })();

  const mergeableOrderIds = new Set(mergeGroups.flat().map((o) => o._id));

  const handleToggleMergeSelect = (orderId) => {
    setSelectedMerge((prev) => {
      const s = new Set(prev);
      s.has(orderId) ? s.delete(orderId) : s.add(orderId);
      return s;
    });
  };

  const canMergeSelection = (() => {
    if (selectedMerge.size < 2) return false;
    const selected = orders.filter((o) => selectedMerge.has(o._id));
    const farmers = new Set(selected.map((o) => o.farmer._id));
    const methods = new Set(selected.map((o) => o.paymentMethod));
    return (
      farmers.size === 1 &&
      methods.size === 1 &&
      selected.every((o) => o.status === "pending")
    );
  })();

  const handleMergeOrders = () => {
    if (!canMergeSelection) return;
    setConfirmDialog({
      open: true,
      title: t("myOrders.confirmMergeTitle"),
      message: t("myOrders.confirmMergeMessage"),
      onConfirm: async () => {
        setConfirmDialog((d) => ({ ...d, open: false }));
        setMergingOrders(true);
        try {
          await api.put("/orders/merge", {
            orderIds: Array.from(selectedMerge),
          });
          showToast(t("myOrders.mergeSuccess"), "success");
          setSelectedMerge(new Set());
          fetchOrders();
        } catch (err) {
          showToast(
            err.response?.data?.message || "Failed to merge orders",
            "error",
          );
        } finally {
          setMergingOrders(false);
        }
      },
    });
  };

  const filters = [
    { key: "all", label: t("myOrders.filterAll"), count: orders.length },
    {
      key: "pending",
      label: t("myOrders.filterPending"),
      count: orders.filter((o) => o.status === "pending").length,
    },
    {
      key: "processing",
      label: t("myOrders.filterProcessing"),
      count: orders.filter((o) =>
        ["processing", "confirmed"].includes(o.status),
      ).length,
    },
    {
      key: "outForDelivery",
      label: t("myOrders.filterOutForDelivery"),
      count: orders.filter((o) =>
        ["shipped", "outForDelivery"].includes(o.status),
      ).length,
    },
    {
      key: "delivered",
      label: t("myOrders.filterDelivered"),
      count: orders.filter((o) => o.status === "delivered").length,
    },
    {
      key: "received",
      label: t("myOrders.filterReceived"),
      count: orders.filter((o) => o.status === "received").length,
    },
    {
      key: "completed",
      label: t("common.status.completed"),
      count: orders.filter((o) => o.status === "completed").length,
    },
    {
      key: "cancelled",
      label: t("myOrders.filterCancelled"),
      count: orders.filter((o) => o.status === "cancelled").length,
    },
  ];

  const stats = [
    {
      value: orders.length,
      label: t("myOrders.statTotal"),
      icon: FaShoppingBag,
      cls: "mo-stat--total",
    },
    {
      value: orders.filter((o) => o.status === "pending").length,
      label: t("myOrders.statPending"),
      icon: FaHourglassHalf,
      cls: "mo-stat--pending",
    },
    {
      value: orders.filter((o) =>
        ["processing", "confirmed"].includes(o.status),
      ).length,
      label: t("myOrders.statProcessing"),
      icon: FaClock,
      cls: "mo-stat--process",
    },
    {
      value: orders.filter((o) => o.status === "delivered").length,
      label: t("myOrders.statDelivered"),
      icon: FaCheckCircle,
      cls: "mo-stat--done",
    },
    {
      value: orders.filter((o) => o.status === "received").length,
      label: t("myOrders.statReceived"),
      icon: FaHandHolding,
      cls: "mo-stat--received",
    },
  ];

  return (
    <div className="mo-page">
      <Header />

      <div className="mo-container">
        {/* Header */}
        <div className="mo-header">
          <div>
            <h1>{t("myOrders.title")}</h1>
            <p>{t("myOrders.subtitle")}</p>
          </div>
        </div>

        <div
          className="mo-list-tabs"
          role="tablist"
          aria-label={t("myOrders.title")}
        >
          <button
            type="button"
            role="tab"
            aria-selected={listView === "active"}
            className={`mo-tab ${listView === "active" ? "mo-tab--on" : ""}`}
            onClick={() => setListView("active")}
          >
            {t("myOrders.tabActive")}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={listView === "archive"}
            className={`mo-tab ${listView === "archive" ? "mo-tab--on" : ""}`}
            onClick={() => setListView("archive")}
          >
            {t("myOrders.tabArchive")}
          </button>
        </div>

        {loading ? (
          <div className="mo-loading">
            <div className="mo-dots">
              <span />
              <span />
              <span />
            </div>
            <p>{t("myOrders.loading")}</p>
          </div>
        ) : (
          <>
            {listView === "active" && (
              <div className="mo-stats">
                {stats.map((s, i) => {
                  const Icon = s.icon;
                  return (
                    <div key={i} className={`mo-stat ${s.cls}`}>
                      <div className="mo-stat-icon">
                        <Icon />
                      </div>
                      <div className="mo-stat-data">
                        <span className="mo-stat-num">
                          {toLocalizedNumber(s.value)}
                        </span>
                        <span className="mo-stat-label">{s.label}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {listView === "archive" && (
              <p className="mo-archive-hint">{t("myOrders.archiveHint")}</p>
            )}

            {/* Search + Filters */}
            <div className="mo-controls">
              <div className="mo-search">
                <FaSearch className="mo-search-icon" />
                <input
                  type="text"
                  placeholder={t("myOrders.searchPlaceholder")}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                {searchTerm && (
                  <button
                    className="mo-search-clear"
                    onClick={() => setSearchTerm("")}
                  >
                    <FaTimes />
                  </button>
                )}
              </div>
              <div className="mo-pills">
                {filters.map((f) => (
                  <button
                    key={f.key}
                    className={`mo-pill ${filter === f.key ? "mo-pill--on" : ""}`}
                    onClick={() => setFilter(f.key)}
                  >
                    {f.label}
                    <span className="mo-pill-count">
                      {toLocalizedNumber(f.count)}
                    </span>
                  </button>
                ))}
              </div>
              <p className="mo-showing">
                {t("myOrders.showing", {
                  showing: toLocalizedNumber(filteredOrders.length),
                  total: toLocalizedNumber(orders.length),
                })}
              </p>
            </div>

            {/* Merge Banner */}
            {listView === "active" && mergeGroups.length > 0 && (
              <div className="mo-merge-banner">
                <div className="mo-merge-info">
                  <FaObjectGroup className="mo-merge-info-icon" />
                  <div>
                    <strong>{t("myOrders.mergeAvailable")}</strong>
                    <p>
                      {t("myOrders.mergeHint", {
                        count: mergeGroups.reduce((s, g) => s + g.length, 0),
                      })}
                    </p>
                  </div>
                </div>
                <div className="mo-merge-actions">
                  {selectedMerge.size > 0 && (
                    <span className="mo-merge-count">
                      {t("myOrders.mergeSelected", {
                        count: selectedMerge.size,
                      })}
                    </span>
                  )}
                  {selectedMerge.size > 0 && (
                    <button
                      className="mo-btn mo-btn--outline"
                      onClick={() => setSelectedMerge(new Set())}
                    >
                      <FaTimes /> {t("myOrders.mergeClear")}
                    </button>
                  )}
                  <button
                    className="mo-btn mo-btn--merge"
                    onClick={handleMergeOrders}
                    disabled={!canMergeSelection || mergingOrders}
                  >
                    <FaObjectGroup />{" "}
                    {mergingOrders
                      ? t("myOrders.merging")
                      : t("myOrders.mergeBtn")}
                  </button>
                </div>
              </div>
            )}

            {/* Orders */}
            {filteredOrders.length === 0 ? (
              <div className="mo-empty">
                <FaBoxOpen className="mo-empty-icon" />
                <h2>{t("myOrders.noOrdersFound")}</h2>
                <p>
                  {listView === "archive"
                    ? t("myOrders.noArchivedOrders")
                    : t("myOrders.noOrdersMatch")}
                </p>
                <Link to="/market" className="mo-btn mo-btn--primary">
                  {t("myOrders.browseMarketplace")}
                </Link>
              </div>
            ) : (
              <div className="mo-list">
                {pageOrders.map((order) => {
                  const meta = statusMeta[order.status] || statusMeta.pending;
                  const StatusIcon = meta.icon;
                  const isOpen = expandedOrders.has(order._id);

                  return (
                    <article
                      key={order._id}
                      className={`mo-card ${isOpen ? "mo-card--open" : ""} ${selectedMerge.has(order._id) ? "mo-card--merge-selected" : ""}`}
                    >
                      {/* Header */}
                      <div
                        className="mo-card-head"
                        onClick={() => toggleOrder(order._id)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            toggleOrder(order._id);
                          }
                        }}
                      >
                        <div className="mo-card-left">
                          {mergeableOrderIds.has(order._id) && (
                            <label
                              className="mo-merge-check"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={selectedMerge.has(order._id)}
                                onChange={() =>
                                  handleToggleMergeSelect(order._id)
                                }
                              />
                              <span className="mo-merge-checkmark" />
                            </label>
                          )}
                          {order.items?.[0] && (
                            <div className="mo-card-thumb">
                              <img
                                src={getProductImage(order.items[0])}
                                alt=""
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/images/deafultpic.png";
                                }}
                              />
                            </div>
                          )}
                          <div className="mo-card-meta">
                            <span className="mo-card-id">
                              #{order._id.slice(-8).toUpperCase()}
                            </span>
                            <span className="mo-card-name">
                              {order.items.length === 1
                                ? order.items[0].name
                                : `${order.items[0]?.name} +${toLocalizedNumber(order.items.length - 1)} more`}
                            </span>
                            <span className="mo-card-date">
                              {formatDate(order.orderDate)}
                            </span>
                          </div>
                        </div>
                        <div className="mo-card-right">
                          <span className="mo-card-total">
                            Rs. {toLocalizedPrice(order.totalAmount)}
                          </span>
                          {order.paymentStatus !== "paid" &&
                            order.status !== "cancelled" && (
                              <span className="mo-badge mo-badge--unpaid">
                                {t("myOrders.unpaid")}
                              </span>
                            )}
                          {order.paymentStatus === "paid" && (
                            <span className="mo-badge mo-badge--paid">
                              {t("myOrders.paid")}
                            </span>
                          )}
                          <span
                            className="mo-badge"
                            style={{ color: meta.color, background: meta.bg }}
                          >
                            <StatusIcon /> {toLocalizedStatus(order.status, t)}
                          </span>
                          <span className="mo-toggle">
                            {isOpen ? <FaChevronUp /> : <FaChevronDown />}
                          </span>
                        </div>
                      </div>

                      {/* Body */}
                      {isOpen && (
                        <div className="mo-card-body">
                          {/* Items */}
                          <div className="mo-items">
                            {order.items.map((item) => (
                              <div key={item._id} className="mo-item">
                                <Link
                                  to={`/products/${item._id}`}
                                  className="mo-item-img"
                                >
                                  <img
                                    src={getProductImage(item)}
                                    alt={item.name}
                                    onError={(e) => {
                                      e.target.onerror = null;
                                      e.target.src = "/images/deafultpic.png";
                                    }}
                                  />
                                </Link>
                                <div className="mo-item-info">
                                  <Link
                                    to={`/products/${item._id}`}
                                    className="mo-item-name"
                                  >
                                    {item.name}
                                  </Link>
                                  <span className="mo-item-qty">
                                    {toLocalizedNumber(item.quantity)}{" "}
                                    {item.unit} × Rs.{" "}
                                    {toLocalizedPrice(item.price)}
                                  </span>
                                </div>
                                <span className="mo-item-total">
                                  Rs.{" "}
                                  {toLocalizedPrice(item.price * item.quantity)}
                                </span>
                              </div>
                            ))}
                          </div>

                          {/* Details */}
                          <div className="mo-details">
                            <div className="mo-detail-grid">
                              <div className="mo-detail">
                                <span className="mo-detail-label">
                                  <img
                                    src={ROLE_IMG.farmer()}
                                    alt=""
                                    className="mo-detail-role-icon"
                                  />
                                  {t("myOrders.farmerLabel")}
                                </span>
                                <span className="mo-detail-value">
                                  {order.farmer.name}
                                </span>
                              </div>
                              <div className="mo-detail">
                                <span className="mo-detail-label">
                                  <FaMapMarkerAlt />{" "}
                                  {t("myOrders.farmerAddressLabel")}
                                </span>
                                <span className="mo-detail-value">
                                  {order.farmer.location}
                                </span>
                              </div>
                              <div className="mo-detail">
                                <span className="mo-detail-label">
                                  <FaMapMarkerAlt />{" "}
                                  {t("myOrders.deliveryAddressLabel")}
                                </span>
                                <span className="mo-detail-value">
                                  {order.deliveryAddress}
                                </span>
                              </div>
                              {order.deliveryDate && (
                                <div className="mo-detail">
                                  <span className="mo-detail-label">
                                    <FaCheckCircle />{" "}
                                    {t("myOrders.deliveredOn")}
                                  </span>
                                  <span className="mo-detail-value">
                                    {formatDate(order.deliveryDate)}
                                  </span>
                                </div>
                              )}
                              {order.estimatedDelivery && (
                                <div className="mo-detail">
                                  <span className="mo-detail-label">
                                    <FaTruck />{" "}
                                    {t("myOrders.estimatedDelivery")}
                                  </span>
                                  <span className="mo-detail-value">
                                    {formatDate(order.estimatedDelivery)}
                                  </span>
                                </div>
                              )}
                              {order.cancellationReason && (
                                <div className="mo-detail">
                                  <span className="mo-detail-label">
                                    <FaTimesCircle />{" "}
                                    {t("myOrders.cancellationReason")}
                                  </span>
                                  <span className="mo-detail-value mo-detail--error">
                                    {order.cancellationReason}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="mo-total-box">
                              <span className="mo-total-label">
                                {t("myOrders.totalAmount")}
                              </span>
                              <span className="mo-total-val">
                                Rs. {toLocalizedPrice(order.totalAmount)}
                              </span>
                            </div>
                          </div>

                          {/* Actions */}
                          <div className="mo-actions">
                            {listView === "active" &&
                              canConsumerCancelOrder(order) && (
                                <button
                                  type="button"
                                  className="mo-btn mo-btn--outline-warn"
                                  onClick={() => handleCancelOrder(order._id)}
                                  disabled={cancellingOrderId === order._id}
                                >
                                  <FaTimesCircle />{" "}
                                  {cancellingOrderId === order._id
                                    ? t("myOrders.cancellingOrder")
                                    : t("myOrders.cancelOrder")}
                                </button>
                              )}
                            {listView === "active" &&
                              order.status === "cancelled" && (
                                <button
                                  type="button"
                                  className="mo-btn mo-btn--outline-warn"
                                  onClick={() => handleRemoveOrder(order._id)}
                                >
                                  <FaTrash /> {t("myOrders.removeFromList")}
                                </button>
                              )}
                            {listView === "active" &&
                              canArchiveOrder(order) && (
                                <button
                                  type="button"
                                  className="mo-btn mo-btn--outline"
                                  onClick={() => handleArchiveOrder(order._id)}
                                >
                                  <FaArchive /> {t("myOrders.archiveOrder")}
                                </button>
                              )}
                            {listView === "archive" && (
                              <button
                                type="button"
                                className="mo-btn mo-btn--outline"
                                onClick={() => handleUnarchiveOrder(order._id)}
                              >
                                <FaUndo /> {t("myOrders.unarchiveOrder")}
                              </button>
                            )}
                            <Link
                              to={`/order/${order._id}`}
                              className="mo-btn mo-btn--primary"
                            >
                              {t("myOrders.trackOrder")} <FaArrowRight />
                            </Link>
                            {order.status === "delivered" && (
                              <button
                                className="mo-btn mo-btn--received"
                                onClick={() => handleConfirmReceived(order._id)}
                                disabled={confirmingOrderId === order._id}
                              >
                                <FaHandHolding />{" "}
                                {confirmingOrderId === order._id
                                  ? t("myOrders.confirmingReceived")
                                  : t("myOrders.markReceived")}
                              </button>
                            )}
                            {canPayAgain(order) && order.paymentGateway && (
                              <button
                                className={`mo-btn ${order.paymentGateway === "esewa" ? "mo-btn--esewa" : "mo-btn--khalti"}`}
                                onClick={() =>
                                  handlePayAgain(
                                    order._id,
                                    order.paymentGateway,
                                  )
                                }
                                disabled={payingOrderId === order._id}
                              >
                                <FaCreditCard />{" "}
                                {payingOrderId === order._id
                                  ? t("myOrders.redirecting")
                                  : t("myOrders.payAgain")}
                              </button>
                            )}
                            {canPayAgain(order) && !order.paymentGateway && (
                              <>
                                <button
                                  className="mo-btn mo-btn--esewa"
                                  onClick={() =>
                                    handlePayAgain(order._id, "esewa")
                                  }
                                  disabled={payingOrderId === order._id}
                                >
                                  <FaCreditCard />{" "}
                                  {payingOrderId === order._id
                                    ? t("myOrders.redirecting")
                                    : t("myOrders.payWithEsewa")}
                                </button>
                                <button
                                  className="mo-btn mo-btn--khalti"
                                  onClick={() =>
                                    handlePayAgain(order._id, "khalti")
                                  }
                                  disabled={payingOrderId === order._id}
                                >
                                  <FaCreditCard />{" "}
                                  {payingOrderId === order._id
                                    ? t("myOrders.redirecting")
                                    : t("myOrders.payWithKhalti")}
                                </button>
                              </>
                            )}
                            <button
                              className="mo-btn mo-btn--outline-warn"
                              onClick={() => {
                                setReportOrderId(order._id);
                                setReportOrderNumber(order._id.slice(-10));
                              }}
                            >
                              <FaFlag /> {t("myOrders.reportIssue")}
                            </button>
                            <Link
                              to={`/messages?farmer=${order.farmer?._id || ""}`}
                              className="mo-btn mo-btn--outline"
                            >
                              <FaComments /> {t("myOrders.messageFarmer")}
                            </Link>
                          </div>
                        </div>
                      )}
                    </article>
                  );
                })}
              </div>
            )}

            <Pagination
              page={page}
              totalPages={totalPages}
              onPageChange={setPage}
            />

            {reportOrderId && (
              <ReportOrderModal
                orderId={reportOrderId}
                orderNumber={reportOrderNumber}
                onClose={() => {
                  setReportOrderId(null);
                  setReportOrderNumber(null);
                }}
                onSuccess={() => fetchOrders()}
              />
            )}

            <ConfirmDialog
              open={confirmDialog.open}
              title={confirmDialog.title}
              message={confirmDialog.message}
              confirmLabel={t("common.confirm")}
              cancelLabel={t("common.cancel")}
              onConfirm={confirmDialog.onConfirm}
              onCancel={() => setConfirmDialog((d) => ({ ...d, open: false }))}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default MyOrders;
